import axios, {
  AxiosError,
  AxiosHeaders,
  type InternalAxiosRequestConfig,
  type RawAxiosRequestHeaders,
} from "axios";

import { resolveAnonymousGet } from "@/modules/anonymousBrowsing/anonymousApiStubs";

import { isAuthenticatedOnlyGetPath } from "./authenticatedOnlyGetPaths";
import { storage } from "@/modules/storage";

import { ApiResponseError, logApiError } from "./errorLogging";
import { derefPath, queryKeyFor, toTemplateMatcher } from "./queryKeys";
import type {
  DeletePaths,
  DeleteRequest,
  GetPaths,
  GetRequest,
  GetResult,
  PatchPaths,
  PatchRequest,
  PatchResult,
  PostPaths,
  PostRequest,
  PostResult,
  PutPaths,
  PutRequest,
  PutResult,
  RawGetResult,
  RawPatchResult,
  RawPostResult,
  RawPutResult,
} from "./types";

// Set app version header for all HTTP requests via interceptor
const appVersion = import.meta.env.REACT_APP_VERSION || "unknown";
axios.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  config.headers["X-App-Version"] = appVersion;
  return config;
});

export const getApiBaseUrl = () => {
  return storage.getItem("baseApiUrl") || import.meta.env.REACT_APP_API_BASE_URL || "";
};

/**
 * The current bearer token, set on the shared axios instance's defaults by
 * `AuthService` after login. Exposed so callers needing a raw `Authorization`
 * header (e.g. `rawPostText`) never have to import `axios` themselves.
 */
export const getAuthorizationHeader = (): string | undefined =>
  axios.defaults.headers.common["Authorization"] as string | undefined;

/**
 * Set or clear the shared bearer token, mirroring how `AuthService` writes it
 * in production. Exists so tests (barred from importing the axios singleton)
 * can put the api layer into a signed-in or signed-out state.
 */
export const setAuthorizationHeader = (token: string | null): void => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

/**
 * A client-synthesized 401 for an authenticated-only GET attempted with no
 * bearer token: the same AxiosError shape (isAxiosError, response.status 401)
 * a backend rejection produces, so the layout error boundaries redirect to
 * /signin exactly as before — without the doomed round-trip.
 */
export const signedOutUnauthorizedError = (resolvedPath: string): AxiosError => {
  const config: InternalAxiosRequestConfig = {
    url: resolvedPath,
    method: "get",
    headers: new AxiosHeaders(),
  };
  return new AxiosError(
    "Request failed with status code 401",
    AxiosError.ERR_BAD_REQUEST,
    config,
    undefined,
    {
      data: {
        error: "invalid_token",
        error_description: "No bearer token present; the request was not sent.",
      },
      status: 401,
      statusText: "Unauthorized",
      headers: {},
      config,
    },
  );
};

/**
 * Perform a `GET` request against a known patient app resource
 */
export const get = async <Path extends GetPaths>(
  path: Path,
  ...[args]: GetRequest<Path> extends undefined ? [] : [GetRequest<Path>]
) => {
  // Anonymous browsing: authenticated-only paths are answered locally by the
  // explicit anonymous contract (see `anonymousApiStubs`) instead of a 401.
  // A `null` stub is the same "no content" value the 204 branch below returns.
  const anonymous = resolveAnonymousGet(path, args);
  if (anonymous) return anonymous.value as GetResult<Path>;

  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});

  // Signed out with anonymous browsing unavailable (the stubs above answer it
  // when active): an authenticated-only path can only 401, so fail fast with
  // that same 401 locally. Route loaders fire these in parallel on every
  // signed-out visit, previously sending ~16 doomed requests per page load.
  if (getAuthorizationHeader() === undefined && isAuthenticatedOnlyGetPath(path)) {
    throw signedOutUnauthorizedError(resolvedPath);
  }

  try {
    const { data: response, status } = await axios.get<RawGetResult<Path>>(resolvedPath, {
      headers: { "Cache-Control": "no-cache" },
      params,
    });

    // A 204 No Content is a valid empty result. Backend endpoints such as
    // GET /incentives/{platformLocationId}/active-signup-reward return Ok(null),
    // which ASP.NET Core serialises as 204. Treat it as null instead of throwing
    // so callers (and React Query retries) do not treat "no content" as a failure.
    if (status === 204) {
      return null as unknown as GetResult<Path>;
    }

    const responseIsObject = response !== null && typeof response === "object";
    const responseHasOwnResult =
      responseIsObject && Object.prototype.hasOwnProperty.call(response, "result");
    if (responseHasOwnResult && (response as { result: unknown }).result != null) {
      return (response as { result: GetResult<Path> }).result;
    } else if (status === 200 && responseIsObject && !responseHasOwnResult) {
      return response as GetResult<Path>;
    } else {
      throw new ApiResponseError(`Failed to load api get result for ${path}`, response, status);
    }
  } catch (error) {
    logApiError("GET", path, resolvedPath, args ?? {}, "queryParams", error);
    throw error;
  }
};

/**
 * Perform a `GET` request against a known patient app resource. Prefer using
 * `get` instead of this function. This function is useful to pass custom headers.
 */
export const rawGet = async <Path extends GetPaths>(
  path: Path,
  args: GetRequest<Path>,
  headers?: RawAxiosRequestHeaders,
): Promise<GetResult<Path>> => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});

  try {
    const { data, status } = await axios.get<RawGetResult<Path>>(resolvedPath, {
      params,
      headers,
    });

    // A 204 No Content is a valid empty result (see `get` above); return null
    // rather than throwing so "no content" is not treated as a failure.
    if (status === 204) {
      return null as unknown as GetResult<Path>;
    }

    const dataIsObject = data !== null && typeof data === "object";
    const dataHasOwnResult = dataIsObject && Object.prototype.hasOwnProperty.call(data, "result");
    if (dataHasOwnResult && (data as { result: unknown }).result != null) {
      return (data as { result: GetResult<Path> }).result;
    } else if (status === 200 && dataIsObject && !dataHasOwnResult) {
      return data as GetResult<Path>;
    } else {
      const error = new ApiResponseError(`Failed to load api get result for ${path}`, data, status);
      throw error;
    }
  } catch (error) {
    logApiError("GET", path, resolvedPath, args ?? {}, "queryParams", error);
    throw error;
  }
};

/**
 * Perform a `POST` request against a known patient app resource
 */
export const post = async <Path extends PostPaths>(
  path: Path,
  args: PostRequest<Path>,
  token?: string | null | undefined,
  headers?: RawAxiosRequestHeaders,
) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});

  const requestBody = "$data" in params ? params.$data : params;

  try {
    const { data: response, status } = await axios.post<RawPostResult<Path>>(
      resolvedPath,
      requestBody,
      {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...headers,
        },
      },
    );
    const successStatusCodes = new Set([200, 201, 204]);

    if (successStatusCodes.has(status) && typeof response === "string") {
      return response as PostResult<Path>;
    } else if (
      successStatusCodes.has(status) &&
      response &&
      typeof response === "object" &&
      "result" in (response as Record<string, unknown>)
    ) {
      return (response as unknown as { result: PostResult<Path> }).result;
    } else if (successStatusCodes.has(status)) {
      return response as PostResult<Path>;
    } else {
      throw new ApiResponseError(`Failed to load api post result for ${path}`, response, status);
    }
  } catch (error) {
    logApiError("POST", path, resolvedPath, requestBody, "requestPayload", error);
    throw error;
  }
};

/**
 * Perform a `POST` request against a known patient app resource and return
 * the raw response body as a string, untouched by the `result`-envelope
 * unwrapping that `post` does. Some partner-EMR endpoints return a string
 * that itself needs a second, manual `JSON.parse` rather than the auto-parsed
 * JSON axios would otherwise hand back, so this forces `responseType: "text"`.
 */
export const rawPostText = async <Path extends PostPaths>(
  path: Path,
  args: PostRequest<Path>,
  headers?: RawAxiosRequestHeaders,
): Promise<string> => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, params] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});
  const requestBody = "$data" in params ? params.$data : params;

  try {
    const { data } = await axios.post<string>(resolvedPath, requestBody, {
      headers,
      responseType: "text",
    });
    return data;
  } catch (error) {
    logApiError("POST", path, resolvedPath, requestBody, "requestPayload", error);
    throw error;
  }
};

/**
 * Perform a `PUT` request against a known patient app resource
 */
export const put = async <Path extends PutPaths>(path: Path, args: PutRequest<Path>) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, data] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});

  const requestBody = Array.isArray(data.$data) ? data.$data : data;

  try {
    const { data: response, status } = await axios.put<RawPutResult<Path>>(
      resolvedPath,
      requestBody,
      { headers: { "Content-Type": "application/json" } },
    );

    const responseIsObject = response !== null && typeof response === "object";
    const responseHasOwnResult =
      responseIsObject && Object.prototype.hasOwnProperty.call(response, "result");
    if (responseHasOwnResult && (response as unknown as { result: unknown }).result !== undefined) {
      return (response as unknown as { result: unknown }).result as PutResult<Path>;
    }
    if (status === 200 && responseIsObject && !responseHasOwnResult) {
      // Typed endpoints outside the legacy BaseCommand envelope (e.g. the membership-freeze
      // resume returning DisplayMembershipFreeze) respond with the DTO directly — mirror the
      // `get` helper and accept the raw object.
      return response as PutResult<Path>;
    }
    if (status === 204) {
      return undefined as PutResult<Path>;
    }
    throw new ApiResponseError(`Failed to load api put result for ${path}`, response, status);
  } catch (error) {
    logApiError("PUT", path, resolvedPath, requestBody, "requestPayload", error);
    throw error;
  }
};

/**
 * Perform a `DELETE` request against a known patient app resource
 */
export const del = async <Path extends DeletePaths>(path: Path, args: DeleteRequest<Path>) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, data] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});

  const requestBody = "$data" in data ? data.$data : data;

  try {
    await axios.delete(resolvedPath, {
      headers: { "Content-Type": "application/json" },
      data: requestBody,
    });
  } catch (error) {
    logApiError("DELETE", path, resolvedPath, requestBody, "requestPayload", error);
    throw error;
  }
};

/**
 * Perform a `PATCH` request against a known patient app resource
 */
export const patch = async <Path extends PatchPaths>(path: Path, args: PatchRequest<Path>) => {
  const baseUrl = getApiBaseUrl();
  const [resolvedPath, data] = derefPath(`${baseUrl}/api/v1${path}`, args ?? {});

  const requestBody = "$data" in data ? data.$data : data;

  try {
    const { data: response, status } = await axios.patch<RawPatchResult<Path>>(
      resolvedPath,
      requestBody,
      { headers: { "Content-Type": "application/json" } },
    );

    const successStatusCodes = new Set([200, 201, 204]);

    if (successStatusCodes.has(status) && typeof response === "string") {
      return response as PatchResult<Path>;
    } else if (
      successStatusCodes.has(status) &&
      response &&
      typeof response === "object" &&
      "result" in (response as Record<string, unknown>)
    ) {
      return (response as unknown as { result: PatchResult<Path> }).result;
    } else if (successStatusCodes.has(status)) {
      return response as PatchResult<Path>;
    } else {
      throw new ApiResponseError(`Failed to load api patch result for ${path}`, response, status);
    }
  } catch (error) {
    logApiError("PATCH", path, resolvedPath, requestBody, "requestPayload", error);
    throw error;
  }
};

export const pget = <P extends GetPaths>(path: P) => {
  type Args = GetRequest<P> extends undefined ? [] : [GetRequest<P>];

  // @ts-expect-error we're summersaulting around the type system here
  return Object.assign((...[args]: Args) => get(path, args), {
    key: (...[args]: Args) => queryKeyFor(path, args as Record<string, unknown> | undefined),
    keyed: (...[args]: Args) => ({
      queryKey: queryKeyFor(path, args as Record<string, unknown> | undefined),
      // @ts-expect-error see above
      queryFn: () => get(path, args),
    }),
    /**
     * Query filters matching this endpoint for any query params and any path params not given —
     * `filters()` is every id, `filters({ platformId })` is every id under that platform. This
     * replaces matching `queryKey[0]` against the template path, which the resolved-URL key shape
     * cannot express. For one exact request use `key(args)`: as a prefix it already matches every
     * query-param variant of that URL.
     */
    filters: (known: Partial<Record<string, unknown>> = {}) => {
      const matcher = toTemplateMatcher(path, known);
      return {
        predicate: (query: { queryKey: readonly unknown[] }) =>
          typeof query.queryKey[0] === "string" && matcher.test(query.queryKey[0]),
      };
    },
  });
};

export const pput = <P extends PutPaths>(path: P) => {
  return (data: Parameters<typeof put<P>>[1]) => put(path, data);
};

export const ppost = <P extends PostPaths>(path: P) => {
  return (data: Parameters<typeof post<P>>[1]) => post(path, data);
};

export const pdel = <P extends DeletePaths>(path: P) => {
  return (data: Parameters<typeof del<P>>[1]) => del(path, data);
};

export const ppatch = <P extends PatchPaths>(path: P) => {
  return (data: Parameters<typeof patch<P>>[1]) => patch(path, data);
};

export type { GetResult, PostResult, PatchResult, PutRequest, DeleteRequest } from "./types";

export { infiniteQueryKey, queryKeyFor, toTemplateMatcher } from "./queryKeys";
